import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, effect, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

import {
    Accessory,
    Equipment,
    Fai,
    Option,
    Phone,
    Plan,
    ProductFactory,
    QuoteModel,
    Sensation,
    Sim,
    Simo,
    Sowo,
} from '@bytel/bytel-sales';
import { CommercialSeries, QuoteContextRenewalModel } from '@bytel/checkout';

import { AppService } from '@services/app.services';
import { Ftth3ClicsService } from '@services/ftth3-clics.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { MixedCartService } from '@services/mixed-cart.service';
import { SimService } from '@services/sim.service';

import { SimContextModel } from '@models/sim-context.model';

import { BasicCrosssellsComponent } from '@tunnel/cart/basic-crosssells/basic-crosssells.component';

import { BasicObject } from '@interfaces/checkout.interface';
import { ISim } from '@interfaces/sim.interace';

import { CmsBlockComponent } from '../../../common/components/cms-block/cms-block.component';
import { DataCyDirective } from '../../../common/directives/data-cy.directive';
import { IndianaPipe } from '../../../shared/pipes/indiana.pipe';
import { SmartTVPipe } from '../../../shared/pipes/smart-tv.pipe';
import { TabletPipe } from '../../../shared/pipes/tablet-pipe';
import { BasicCrosssellsComponent as BasicCrosssellsComponent_1 } from '../basic-crosssells/basic-crosssells.component';
import { EmptyCartItemComponent } from '../empty-cart-item/empty-cart-item.component';
import { EquipmentBlockComponent } from '../equipment-block/equipment-block.component';
import { InsurancesComponent } from '../insurances/insurances.component';
import { OfferPepitesComponent } from '../offer-pepites/offer-pepites.component';
import { ParentalControlComponent } from '../parental-control/parental-control.component';
import { PortabilityComponent } from '../portability/portability.component';
import { SimComponent } from '../sim/sim.component';
import { SubscriptionBlockComponent } from '../subscription-block/subscription-block.component';

@Component({
    selector: 'checkout-quote-detail',
    templateUrl: './quote-detail.component.html',
    styleUrl: 'quote-detail.component.scss',
    standalone: true,
    imports: [
        NgClass,
        SubscriptionBlockComponent,
        EmptyCartItemComponent,
        EquipmentBlockComponent,
        BasicCrosssellsComponent_1,
        CmsBlockComponent,
        DataCyDirective,
        PortabilityComponent,
        ParentalControlComponent,
        InsurancesComponent,
        SmartTVPipe,
        TabletPipe,
        IndianaPipe,
        SimComponent,
        OfferPepitesComponent,
    ],
})
export class QuoteDetailComponent implements OnInit {
    @Input() public cartForm: FormGroup;
    @Input() public quoteIndex: number = 1;
    @Input() public isSubmitted: boolean;
    @Input() public mustSuggestMixedCard: boolean = true;
    @Input() public isMobile: boolean = false;
    @Output() public simChoice = new EventEmitter<ISim>();

    public suggestMixedCart: boolean = false;
    public crosssellTypes: BasicObject = BasicCrosssellsComponent.CROSSSELL_TYPES;
    public equipment: Equipment;
    public hasAccessories: boolean = false;
    public isBareCart: boolean = false;
    public isQuoteEmpty: boolean = true;
    public isAccessoriesCart: boolean = false;
    public isFai: boolean = false;
    public isSimo: boolean = false;
    public isFtth3Clics: boolean = false;
    public isBankCart: boolean = false;
    public isRenew: boolean = false;
    public isSas: boolean = false;
    public isSowo: boolean = false;
    public plan: Plan;
    public planPropertiesCmsKey: string;
    public pepitesSlug: string;
    public isPreCrossSell: boolean;
    public isYamaCart: boolean = false;
    public canShowOptions: boolean = true;
    public canHaveMixedCart: boolean = false;
    public isMixedCart: boolean = false;
    public isPreorder: boolean = false;
    public isMobileMQ: boolean = false;
    public sims: ISim[];
    public hasPhone: boolean = false;
    public showOfferPepites: boolean = false;
    public currentSimChoice: ISim;
    public simContext: SimContextModel;
    public isLoading: boolean;
    public hasOptions: boolean = false;
    public currentPlan: Plan | undefined;
    public simContexts: SimContextModel[];
    protected appService: AppService = inject(AppService);
    protected cartService: MainCartService = inject(MainCartService);
    protected ftth3ClicsService: Ftth3ClicsService = inject(Ftth3ClicsService);
    protected messageService: MessageService = inject(MessageService);
    private mixedCartService: MixedCartService = inject(MixedCartService);
    private simService: SimService = inject(SimService);

    constructor() {
        effect(() => {
            this.simContexts = this.simService.simContexts();
            this._getSimContext(this._getQuote());
        });
    }
    public ngOnInit(): void {
        this._refresh();
        this.cartService.cartQuoteSubscription.subscribe(() => {
            this._refresh();
        });
    }

    public onSimChoice = (simChoice: ISim): void => {
        this.currentSimChoice = simChoice;
        this.simChoice.emit(simChoice);
    };

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }

    private _refresh(): void {
        this._setProducts();
        const quote: QuoteModel = this._getQuote();
        this.isRenew = quote.isRenewal();
        this.isQuoteEmpty = quote.products.length === 0;
        this.isBareCart = this.isQuoteEmpty && !this.equipment && !this.plan;
        this.isPreorder = quote.isPreorder();
        this.isSas = ProductFactory.Is(this.plan, Sensation, true);
        this.isSowo = ProductFactory.Is(this.plan, Sowo, true);
        this.isFai = ProductFactory.Is(this.plan, Fai, true);
        this.isSimo = ProductFactory.Is(this.plan, Simo, true);
        this.isBankCart = this.cartService.isBankCart();
        this.isFtth3Clics = this.ftth3ClicsService.isActive();
        this.canHaveMixedCart = this.mixedCartService.canHaveMixedCart(this.cartService.cartModel);
        this.isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);
        this.isAccessoriesCart = !this.equipment && !this.plan && this.hasAccessories;
        this.hasPhone = !!quote.getProductByType(Phone);
        if (this.plan) {
            const key: string = this.isFai ? (this.plan as Fai).data.range_ng : this.plan.data.url_key;
            this.planPropertiesCmsKey = !this.isBankCart && `zone_panier_1_descriptif_${key}`;
            this.currentSimChoice = this.sims?.find(
                (sim) => sim.gencode === this._getQuote().getProductByType(Sim)?.gencode,
            );

            this._checkShowBankingOfferPepites(quote);
        }

        if (this.isRenew) {
            const context = quote.context as QuoteContextRenewalModel;
            this.canShowOptions = this.isYamaCart && context.currentPlan !== this.plan.gencode;
        }

        this.suggestMixedCart =
            this.mustSuggestMixedCard &&
            ((this.mixedCartService.isActive() && this.canHaveMixedCart) || this.isMixedCart);

        this.currentPlan = this.cartService.getQuote(this.quoteIndex).getProductByType<Plan>(Plan);
    }

    private _setProducts(): void {
        const quote: QuoteModel = this._getQuote();
        this.plan = quote.getPrincipalProduct<Plan>('Plan');
        this.equipment = quote.getPrincipalProduct<Equipment>('Equipment');
        const accessories: Accessory[] = quote.getProductsByType<Accessory>('Accessory');
        const options: Option[] = quote.getProductsByType<Option>('Option');
        this.hasAccessories = !!accessories?.length;
        this.hasOptions = !!options?.length;
        this.isPreCrossSell = this.cartService.isPreCrossSell(this.quoteIndex);
    }

    private _getSimContext(quote: QuoteModel): void {
        if (!this.simContexts || !this.simContexts[this.quoteIndex]) {
            return;
        }
        this.simContext = this.simContexts[this.quoteIndex];

        this.simContext = this.simContexts[this.quoteIndex];
        this.sims = this.simContext?.sims;
        const currentCartSim = quote.getProductByType(Sim)?.gencode;
        if (currentCartSim) {
            this.currentSimChoice = this.sims?.find((sim) => sim.gencode === quote.getProductByType(Sim)?.gencode);
        } else {
            this.currentSimChoice = this.sims?.find((sim) => sim.isPrefered);
        }
    }

    private _checkShowBankingOfferPepites(quote: QuoteModel): void {
        if (this.isFai) {
            const subscription: Fai = quote.getPrincipalProduct<Fai>('Fai');
            if (subscription.data.commercial_series === CommercialSeries.BANQUE) {
                this.pepitesSlug = 'offre-banque-fai';
                this.showOfferPepites = true;
            }
        } else if (this.isBankCart) {
            this.pepitesSlug = this.plan.data.url_key;
            this.showOfferPepites = true;
        }
    }
}
